/**
 * The request-size rejections api-service answers with 400 (backend L30-2360,
 * processors/api-service/reportguard/reportguard.go): a date range over 366 days, a GPS-trail
 * (fields[]=coordinates, /vehicles/:id/th) range over 32 days, an id list over 10,000 ids. The body
 * is `{ error: '<message>' }` and the message is one of three constants that name the cap, so the
 * number is read from the message and a cap change on the backend needs no change here. The
 * response carries no machine-readable code for these yet (`ErrorResponse.Code` is unset), hence
 * the message match (L30-2478).
 */
export type RangeLimitError = { kind: 'dateRange'; days: number } | { kind: 'idList'; count: number }

const DATE_RANGE_TOO_LONG = /^date range(?: with coordinates)? must not exceed (\d+) days$/
const TOO_MANY_IDS = /^an id list must not contain more than (\d+) ids$/

// `body` is the error body as HttpClient hands it over: a parsed object for JSON requests, a string
// for responseType 'text' (csv/xlsx exports). A Blob body (responseType 'blob', pdf exports) is read
// to text by the caller first, because reading a Blob is asynchronous.
export function parseRangeLimitError(status: number, body: unknown): RangeLimitError | null {
  if (status !== 400) return null

  const message = backendErrorMessage(body)
  if (!message) return null

  const range = DATE_RANGE_TOO_LONG.exec(message)
  if (range) return { kind: 'dateRange', days: Number(range[1]) }

  const ids = TOO_MANY_IDS.exec(message)
  if (ids) return { kind: 'idList', count: Number(ids[1]) }

  return null
}

// The Go business-error envelope `{ error }`, with `{ message }` as the alternate shape
// FormFeedbackInterceptor also reads. A text body is JSON-parsed; anything that is neither a string
// nor an object is ignored.
function backendErrorMessage(body: unknown): string | null {
  let parsed: unknown = body
  if (typeof body === 'string') {
    try {
      parsed = JSON.parse(body)
    } catch {
      return null
    }
  }
  if (!parsed || typeof parsed !== 'object') return null

  const envelope = parsed as { error?: unknown; message?: unknown }
  const candidate = envelope.error ?? envelope.message
  return typeof candidate === 'string' ? candidate.trim() : null
}
